import { useMemo } from 'react';
import {
  FileText,
  LogIn,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  SquareArrowOutUpRight,
  UploadIcon,
} from 'lucide-react';
import { MenuItem, MenuLabel, MenuProvider, MenuSeparator, dropdownPrimitives } from '@/components/menu-renderer';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AssetManagementNavLink } from '@/components/side-navigation/asset-management-nav-link';
import { LogoutMenuItem, ThemeMenuSubmenu } from '@/components/menu-items/node-menu-items';
import { ShareExportProgressBars } from '@/components/shares/share-export-progress-bars';
import { SideNavigationLinks } from '@/components/side-navigation/side-navigation-links';
import { useUserSettingsStore } from '@/hooks/data/settings/useUserSettingsStore';
import { useAccount } from '@/hooks/data/account/useAccount';
import { useDamNavigation } from '@/hooks/useDamNavigation';
import { useCapabilities } from '@/hooks/useCapabilities';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/router';
import Link from 'next/link';

export const SideNavigation = () => {
  const { profile } = useAccount();
  const { canViewSettings, canViewTerms, canDownloadAssets } = useCapabilities();
  const user = profile?.user;

  const { navigateAndReset } = useDamNavigation();
  const { push } = useRouter();
  const collapseSideNavigation = useUserSettingsStore((s) => s.collapseSideNavigation);
  const setCollapseSideNavigation = useUserSettingsStore((s) => s.setCollapseSideNavigation);
  const { isManagePage, isPublicSharePage, isSubmitPage } = useCurrentPage();
  const tMenu = useTranslations('menu.user');
  const tCommon = useTranslations('common');
  const tNav = useTranslations('menu.nav');

  const topSectionLinks = useMemo(
    () => [
      {
        label: collapseSideNavigation ? 'Open Menu' : 'Close Menu',
        icon: collapseSideNavigation ? PanelLeftOpen : PanelLeftClose,
        selected: false,
        onClick: () => setCollapseSideNavigation(!collapseSideNavigation),
        className: cn('border-t border-t-toolbar-bevel'),
        show: true,
      },
      {
        label: tNav('submission'),
        icon: UploadIcon,
        selected: true,
        className: 'text-text-base',
        show: Boolean(user) && isSubmitPage,
      },
    ],
    [collapseSideNavigation, setCollapseSideNavigation, user, isSubmitPage, tNav],
  );

  const bottomSectionLinks = useMemo(
    () => [
      ...(canViewSettings
        ? [
            {
              label: 'Settings',
              icon: Settings,
              selected: isManagePage,
              className: 'text-text-base',
              onClick: () => {
                void navigateAndReset('/manage', {});
              },
            },
          ]
        : []),
    ],
    [isManagePage, navigateAndReset, canViewSettings],
  );

  return (
    <div className={cn('flex flex-col space-y-px text-icon-base', collapseSideNavigation ? 'w-12' : 'w-64')}>
      {topSectionLinks
        .filter(({ show }) => show)
        .map(({ show, ...props }) => (
          <SideNavigationLinks key={props.label} collapsed={collapseSideNavigation} {...props} />
        ))}
      {user && !isSubmitPage && <AssetManagementNavLink collapsed={collapseSideNavigation} />}
      {/* Filler element */}
      <div className="grow bg-ui-base" />
      {isPublicSharePage && canDownloadAssets && <ShareExportProgressBars collapsed={collapseSideNavigation} />}
      {user &&
        bottomSectionLinks.map((props) => (
          <SideNavigationLinks key={props.label} collapsed={collapseSideNavigation} {...props} />
        ))}
      {user ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SideNavigationLinks
              label={tMenu('settings')}
              collapsed={collapseSideNavigation}
              selected={false}
              interactive
            >
              <>
                <UserAvatar className="size-7!" user={user} />
                {!collapseSideNavigation && (
                  <p className="line-clamp-1 font-light wrap-anywhere text-text-gray">{user.name}</p>
                )}
              </>
            </SideNavigationLinks>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="max-w-56">
            <MenuProvider primitives={dropdownPrimitives}>
              <MenuLabel className="my-1.5 py-0">{user.name}</MenuLabel>
              <MenuSeparator />
              <ThemeMenuSubmenu />
              {canViewTerms && (
                <>
                  <MenuSeparator />

                  <Link target="_blank" href={'/terms'} className="cursor-pointer">
                    <MenuItem icon={FileText} dataCy="view-terms-and-conditions" appendIcon={SquareArrowOutUpRight}>
                      {tMenu('terms&Conditions')}
                    </MenuItem>
                  </Link>
                </>
              )}
              <MenuSeparator />
              <LogoutMenuItem />
            </MenuProvider>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <SideNavigationLinks
          key="login"
          collapsed={collapseSideNavigation}
          label={tCommon('login')}
          selected={false}
          icon={LogIn}
          onClick={() => {
            void push(
              `/identifier?redirect_url=${encodeURIComponent(window.location.pathname + window.location.search)}`,
            );
          }}
        />
      )}
    </div>
  );
};
